/**
 * Premium Landing Page Templates — Commercial Grade (9/10)
 * Each template produces standalone HTML with modern CSS, animations, responsive design.
 * Templates are render functions: (sectionData) → full HTML string
 */

// Curated stock photos per niche
export const NICHE_PHOTOS = {
  business: [
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
    'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80',
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80',
    'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80',
  ],
  creator: [
    'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&q=80',
    'https://images.unsplash.com/photo-1533227268428-f9ed0900fb3b?w=800&q=80',
    'https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=800&q=80',
    'https://images.unsplash.com/photo-1598520106830-8c45c2035460?w=800&q=80',
  ],
  education: [
    'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&q=80',
    'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80',
    'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800&q=80',
    'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&q=80',
  ],
  health: [
    'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80',
    'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800&q=80',
    'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&q=80',
    'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&q=80',
  ],
  food: [
    'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80',
    'https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=800&q=80',
    'https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=800&q=80',
    'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
  ],
};

// Font pairings
export const FONT_PAIRS = [
  { id: 'modern', heading: 'Space Grotesk', body: 'Inter', label: 'Modern Clean' },
  { id: 'editorial', heading: 'Playfair Display', body: 'Lato', label: 'Editorial' },
  { id: 'bold', heading: 'Montserrat', body: 'Open Sans', label: 'Bold Impact' },
  { id: 'elegant', heading: 'Cormorant Garamond', body: 'Raleway', label: 'Elegant' },
  { id: 'minimal', heading: 'Poppins', body: 'Work Sans', label: 'Minimal' },
  { id: 'tech', heading: 'JetBrains Mono', body: 'Inter', label: 'Tech' },
];

// Color palettes
export const COLOR_PALETTES = [
  { id: 'ocean', primary: '#0F766E', accent: '#14B8A6', bg: '#F0FDFA', text: '#0F172A', label: 'Ocean Teal' },
  { id: 'midnight', primary: '#1E293B', accent: '#3B82F6', bg: '#FFFFFF', text: '#0F172A', label: 'Midnight Blue' },
  { id: 'sunset', primary: '#9333EA', accent: '#F97316', bg: '#FEFCE8', text: '#1C1917', label: 'Sunset Glow' },
  { id: 'forest', primary: '#166534', accent: '#22C55E', bg: '#F0FDF4', text: '#14532D', label: 'Forest Green' },
  { id: 'rose', primary: '#9F1239', accent: '#FB7185', bg: '#FFF1F2', text: '#1C1917', label: 'Rose' },
  { id: 'slate', primary: '#334155', accent: '#F59E0B', bg: '#FFFFFF', text: '#0F172A', label: 'Slate Gold' },
  { id: 'dark', primary: '#18181B', accent: '#A78BFA', bg: '#09090B', text: '#FAFAFA', label: 'Dark Mode' },
  { id: 'coral', primary: '#EA580C', accent: '#FBBF24', bg: '#FFFBEB', text: '#1C1917', label: 'Warm Coral' },
];

// Default section data factory per template
export function getDefaultSections(templateId) {
  const base = {
    hero: {
      enabled: true,
      headline: 'Transform Your Business Today',
      subheadline: 'The complete toolkit to grow your digital presence and scale your revenue',
      cta_text: 'Get Started',
      cta_url: '#products',
      image: NICHE_PHOTOS.business[0],
    },
    features: {
      enabled: true,
      title: 'Everything You Need',
      subtitle: 'Powerful features designed to help you succeed',
      items: [
        { icon: 'zap', title: 'Lightning Fast', desc: 'Optimized for speed and performance' },
        { icon: 'shield', title: 'Secure & Reliable', desc: 'Enterprise-grade security built in' },
        { icon: 'star', title: 'Premium Quality', desc: 'Crafted by industry professionals' },
        { icon: 'target', title: 'Results Driven', desc: 'Focused on delivering real outcomes' },
        { icon: 'users', title: 'Community', desc: 'Join thousands of successful users' },
        { icon: 'gift', title: 'Bonus Content', desc: 'Exclusive materials included free' },
      ],
    },
    about: {
      enabled: true,
      title: 'Why Choose Us',
      text: 'We have spent years perfecting our approach. Our products are designed by practitioners who understand real-world challenges.',
      image: NICHE_PHOTOS.business[1],
    },
    testimonials: {
      enabled: true,
      title: 'What Our Customers Say',
      items: [
        { name: 'Sarah Ahmad', role: 'Digital Creator', text: 'This completely transformed how I run my business. The results speak for themselves.', rating: 5 },
        { name: 'Fariz Hassan', role: 'Course Creator', text: 'Best investment I made this year. Simple, effective, and the support is amazing.', rating: 5 },
        { name: 'Aina Sofea', role: 'Freelancer', text: 'Finally found a solution that actually works. Highly recommended to anyone serious about growth.', rating: 5 },
      ],
    },
    cta: {
      enabled: true,
      title: 'Ready to Get Started?',
      subtitle: 'Join thousands who have already transformed their business',
      button_text: 'Start Now',
      button_url: '#products',
    },
    footer: {
      enabled: true,
      text: '',
    },
  };

  // Customize defaults per template
  if (templateId === 'creator-bold') {
    base.hero.headline = 'Your Content. Your Empire.';
    base.hero.subheadline = 'Everything you need to build, grow, and monetize your creator brand';
    base.hero.image = NICHE_PHOTOS.creator[0];
    base.about.image = NICHE_PHOTOS.creator[1];
    base.features.items = [
      { icon: 'zap', title: 'Instant Access', desc: 'Download immediately after purchase' },
      { icon: 'star', title: 'Premium Templates', desc: 'Professional designs ready to use' },
      { icon: 'target', title: 'Growth Tactics', desc: 'Proven strategies for scaling' },
      { icon: 'gift', title: 'Bonus Pack', desc: 'Extra resources included free' },
      { icon: 'users', title: '10K+ Community', desc: 'Connect with fellow creators' },
      { icon: 'shield', title: 'Lifetime Updates', desc: 'Always get the latest version' },
    ];
  } else if (templateId === 'education-warm') {
    base.hero.headline = 'Master New Skills Today';
    base.hero.subheadline = 'Expert-led courses and resources designed for practical, real-world learning';
    base.hero.image = NICHE_PHOTOS.education[0];
    base.about.image = NICHE_PHOTOS.education[1];
  } else if (templateId === 'health-calm') {
    base.hero.headline = 'Your Wellness Journey Starts Here';
    base.hero.subheadline = 'Science-backed guides and programs for a healthier, more balanced life';
    base.hero.image = NICHE_PHOTOS.health[0];
    base.about.image = NICHE_PHOTOS.health[1];
  } else if (templateId === 'dark-luxe') {
    base.hero.headline = 'NEXT LEVEL\nDIGITAL PRODUCTS';
    base.hero.subheadline = 'Premium tools and resources built for ambitious creators who demand the best';
    base.hero.cta_text = 'Get Access';
    base.hero.image = '';
    base.about.title = 'Built Different';
    base.about.text = 'We obsess over quality so you don\'t have to. Every product is tested, refined, and designed for real-world results.';
    base.about.image = 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80';
    base.features.title = 'Why Choose Us';
    base.features.subtitle = 'Built for performance. Designed for you.';
    base.features.items = [
      { icon: 'zap', title: 'Blazing Fast', desc: 'Optimized for maximum performance' },
      { icon: 'shield', title: 'Fort Knox Security', desc: 'Enterprise-grade encryption' },
      { icon: 'target', title: 'Precision Tools', desc: 'Professional-grade quality' },
      { icon: 'star', title: 'Premium Assets', desc: 'Exclusive content included' },
    ];
    base.cta.title = 'Ready to Level Up?';
    base.cta.subtitle = 'Join thousands of creators already using our tools';
    base.cta.button_text = 'Get Started Now';
  }

  return base;
}

// Shared CSS used by all premium templates
const sharedCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700;800&family=Montserrat:wght@400;500;600;700;800;900&family=Poppins:wght@300;400;500;600;700&family=Lato:wght@300;400;700&family=Open+Sans:wght@300;400;600;700&family=Raleway:wght@300;400;500;600;700&family=Cormorant+Garamond:wght@400;500;600;700&family=Work+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
  img { max-width: 100%; height: auto; display: block; }
  a { text-decoration: none; color: inherit; }
  .reveal { opacity: 0; transform: translateY(30px); transition: opacity 0.7s ease, transform 0.7s ease; }
  .reveal.visible { opacity: 1; transform: translateY(0); }
  @keyframes fadeInUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
  @keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.03); } }
  .btn-hover { transition: all 0.3s ease; }
  .btn-hover:hover { transform: translateY(-2px); box-shadow: 0 12px 40px rgba(0,0,0,0.15); }
`;

// Scroll reveal script
const revealScript = `<script>
  document.addEventListener('DOMContentLoaded',function(){
    var els=document.querySelectorAll('.reveal');
    var obs=new IntersectionObserver(function(entries){
      entries.forEach(function(e){if(e.isIntersecting){e.target.classList.add('visible');obs.unobserve(e.target);}});
    },{threshold:0.1,rootMargin:'0px 0px -40px 0px'});
    els.forEach(function(el){obs.observe(el);});
  });
</script>`;

// ======================== TEMPLATE RENDERERS ========================

/**
 * Render premium template HTML from section data
 * @param {string} templateId - Template identifier
 * @param {object} sections - Section data (hero, features, about, testimonials, cta, footer)
 * @param {object} style - { primary, accent, bg, text, fontHeading, fontBody }
 * @param {array} productHtml - Pre-rendered product cards HTML (injected by PublicLandingPage)
 * @returns {string} Full HTML document
 */
export function renderPremiumTemplate(templateId, sections, style, productHtml = '') {
  const s = {
    primary: style.primary || '#0F766E',
    accent: style.accent || '#14B8A6',
    bg: style.bg || '#FFFFFF',
    text: style.text || '#0F172A',
    fh: style.fontHeading || 'Space Grotesk',
    fb: style.fontBody || 'Inter',
  };

  // Feature icons as inline SVG
  const iconSvg = (name, color) => {
    const icons = {
      zap: `<path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>`,
      shield: `<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>`,
      star: `<polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>`,
      target: `<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>`,
      users: `<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>`,
      gift: `<polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>`,
      heart: `<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>`,
      clock: `<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>`,
      check: `<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>`,
    };
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${icons[name] || icons.star}</svg>`;
  };

  // Star rating
  const stars = (count) => Array(count).fill('').map(() => `<svg width="16" height="16" viewBox="0 0 24 24" fill="${s.accent}" stroke="none"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></svg>`).join('');

  // Build sections
  const heroSection = sections.hero?.enabled !== false ? `
    <section style="position:relative;min-height:90vh;display:flex;align-items:center;background:${s.bg};overflow:hidden;">
      <div style="position:absolute;top:0;right:0;width:50%;height:100%;background:linear-gradient(135deg,${s.primary}08,${s.accent}15);border-radius:0 0 0 40%;z-index:0;"></div>
      <div style="max-width:1200px;margin:0 auto;padding:80px 24px;display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:center;position:relative;z-index:1;width:100%;">
        <div style="animation:fadeInUp 0.8s ease forwards;">
          <div style="display:inline-block;padding:6px 16px;border-radius:100px;background:${s.primary}10;color:${s.primary};font-size:13px;font-weight:600;font-family:'${s.fb}',sans-serif;margin-bottom:24px;letter-spacing:0.5px;">Limited Time Offer</div>
          <h1 style="font-family:'${s.fh}',sans-serif;font-size:clamp(36px,5vw,56px);font-weight:800;line-height:1.1;color:${s.text};margin-bottom:24px;">${sections.hero.headline || ''}</h1>
          <p style="font-family:'${s.fb}',sans-serif;font-size:18px;line-height:1.7;color:${s.text}99;margin-bottom:36px;max-width:500px;">${sections.hero.subheadline || ''}</p>
          <div style="display:flex;gap:16px;flex-wrap:wrap;">
            <a href="${sections.hero.cta_url || '#products'}" class="btn-hover" style="display:inline-flex;align-items:center;gap:8px;padding:16px 36px;background:${s.primary};color:#fff;border-radius:12px;font-family:'${s.fb}',sans-serif;font-size:16px;font-weight:600;cursor:pointer;">${sections.hero.cta_text || 'Get Started'} <span style="font-size:18px;">→</span></a>
          </div>
          <div style="display:flex;gap:32px;margin-top:40px;">
            <div><span style="font-family:'${s.fh}',sans-serif;font-size:28px;font-weight:700;color:${s.text};">500+</span><br><span style="font-size:13px;color:${s.text}66;font-family:'${s.fb}',sans-serif;">Happy Customers</span></div>
            <div><span style="font-family:'${s.fh}',sans-serif;font-size:28px;font-weight:700;color:${s.text};">4.9★</span><br><span style="font-size:13px;color:${s.text}66;font-family:'${s.fb}',sans-serif;">Average Rating</span></div>
          </div>
        </div>
        <div style="animation:fadeInUp 0.8s ease 0.2s forwards;opacity:0;">
          <div style="position:relative;">
            <div style="position:absolute;inset:-8px;background:linear-gradient(135deg,${s.primary}30,${s.accent}30);border-radius:24px;transform:rotate(3deg);"></div>
            <img src="${sections.hero.image || ''}" alt="Hero" style="position:relative;width:100%;border-radius:20px;object-fit:cover;aspect-ratio:4/3;box-shadow:0 25px 60px rgba(0,0,0,0.12);" />
          </div>
        </div>
      </div>
      <style>@media(max-width:768px){section>div:nth-child(2){grid-template-columns:1fr!important;text-align:center;}section>div:nth-child(2)>div:last-child{order:-1;}}</style>
    </section>` : '';

  const featuresSection = sections.features?.enabled !== false ? `
    <section class="reveal" style="padding:100px 24px;background:${s.bg};position:relative;">
      <div style="max-width:1100px;margin:0 auto;text-align:center;">
        <h2 style="font-family:'${s.fh}',sans-serif;font-size:clamp(28px,3.5vw,40px);font-weight:700;color:${s.text};margin-bottom:12px;">${sections.features.title || 'Features'}</h2>
        <p style="font-family:'${s.fb}',sans-serif;font-size:17px;color:${s.text}77;margin-bottom:60px;max-width:600px;margin-left:auto;margin-right:auto;">${sections.features.subtitle || ''}</p>
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:32px;">
          ${(sections.features.items || []).map((item, i) => `
            <div class="reveal" style="padding:36px 28px;border-radius:16px;background:${s.primary}05;border:1px solid ${s.primary}10;text-align:center;transition:all 0.3s ease;animation-delay:${i * 0.1}s;">
              <div style="width:52px;height:52px;border-radius:14px;background:${s.primary}12;display:inline-flex;align-items:center;justify-content:center;margin-bottom:20px;">${iconSvg(item.icon, s.primary)}</div>
              <h3 style="font-family:'${s.fh}',sans-serif;font-size:18px;font-weight:600;color:${s.text};margin-bottom:10px;">${item.title}</h3>
              <p style="font-family:'${s.fb}',sans-serif;font-size:14px;line-height:1.6;color:${s.text}77;">${item.desc}</p>
            </div>`).join('')}
        </div>
      </div>
      <style>@media(max-width:768px){section .reveal>div>div:last-child{grid-template-columns:1fr!important;}}</style>
    </section>` : '';

  const aboutSection = sections.about?.enabled !== false ? `
    <section class="reveal" style="padding:100px 24px;background:linear-gradient(180deg,${s.primary}05,${s.bg});">
      <div style="max-width:1100px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:60px;align-items:center;">
        <div>
          <img src="${sections.about.image || ''}" alt="About" style="width:100%;border-radius:20px;object-fit:cover;aspect-ratio:4/3;box-shadow:0 20px 50px rgba(0,0,0,0.08);" />
        </div>
        <div>
          <h2 style="font-family:'${s.fh}',sans-serif;font-size:clamp(28px,3.5vw,36px);font-weight:700;color:${s.text};margin-bottom:20px;">${sections.about.title || ''}</h2>
          <p style="font-family:'${s.fb}',sans-serif;font-size:16px;line-height:1.8;color:${s.text}88;">${sections.about.text || ''}</p>
        </div>
      </div>
    </section>` : '';

  const productsBlock = productHtml ? `
    <section id="products" class="reveal" style="padding:80px 24px;background:${s.bg};">
      <div style="max-width:1100px;margin:0 auto;text-align:center;">
        <h2 style="font-family:'${s.fh}',sans-serif;font-size:clamp(28px,3.5vw,36px);font-weight:700;color:${s.text};margin-bottom:48px;">Products</h2>
        ${productHtml}
      </div>
    </section>` : '';

  const testimonialsSection = sections.testimonials?.enabled !== false ? `
    <section class="reveal" style="padding:100px 24px;background:${s.primary}05;">
      <div style="max-width:1100px;margin:0 auto;text-align:center;">
        <h2 style="font-family:'${s.fh}',sans-serif;font-size:clamp(28px,3.5vw,36px);font-weight:700;color:${s.text};margin-bottom:48px;">${sections.testimonials.title || 'Testimonials'}</h2>
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:24px;">
          ${(sections.testimonials.items || []).map(t => `
            <div style="padding:32px;border-radius:16px;background:${s.bg};border:1px solid ${s.primary}10;text-align:left;box-shadow:0 4px 20px rgba(0,0,0,0.04);">
              <div style="display:flex;gap:2px;margin-bottom:16px;">${stars(t.rating || 5)}</div>
              <p style="font-family:'${s.fb}',sans-serif;font-size:15px;line-height:1.7;color:${s.text}88;margin-bottom:20px;font-style:italic;">"${t.text}"</p>
              <div>
                <p style="font-family:'${s.fh}',sans-serif;font-size:14px;font-weight:600;color:${s.text};">${t.name}</p>
                ${t.role ? `<p style="font-size:12px;color:${s.text}55;font-family:'${s.fb}',sans-serif;">${t.role}</p>` : ''}
              </div>
            </div>`).join('')}
        </div>
      </div>
    </section>` : '';

  const ctaSection = sections.cta?.enabled !== false ? `
    <section class="reveal" style="padding:100px 24px;background:linear-gradient(135deg,${s.primary},${s.primary}DD);text-align:center;position:relative;overflow:hidden;">
      <div style="position:absolute;top:-50%;right:-20%;width:400px;height:400px;border-radius:50%;background:${s.accent}20;"></div>
      <div style="position:absolute;bottom:-30%;left:-10%;width:300px;height:300px;border-radius:50%;background:${s.accent}15;"></div>
      <div style="max-width:700px;margin:0 auto;position:relative;z-index:1;">
        <h2 style="font-family:'${s.fh}',sans-serif;font-size:clamp(28px,4vw,42px);font-weight:700;color:#fff;margin-bottom:16px;">${sections.cta.title || ''}</h2>
        <p style="font-family:'${s.fb}',sans-serif;font-size:17px;color:rgba(255,255,255,0.8);margin-bottom:36px;">${sections.cta.subtitle || ''}</p>
        <a href="${sections.cta.button_url || '#products'}" class="btn-hover" style="display:inline-flex;align-items:center;gap:8px;padding:18px 44px;background:#fff;color:${s.primary};border-radius:12px;font-family:'${s.fb}',sans-serif;font-size:17px;font-weight:700;cursor:pointer;">${sections.cta.button_text || 'Get Started'} →</a>
      </div>
    </section>` : '';

  const footerSection = `
    <footer style="padding:32px 24px;background:${s.text};text-align:center;">
      <p style="font-family:'${s.fb}',sans-serif;font-size:13px;color:rgba(255,255,255,0.4);">${sections.footer?.text || `© ${new Date().getFullYear()} All rights reserved`}</p>
    </footer>`;

  // Responsive overrides
  const responsiveCSS = `
    @media(max-width:900px) {
      [data-grid3] { grid-template-columns: repeat(2,1fr) !important; }
    }
    @media(max-width:640px) {
      [data-grid3] { grid-template-columns: 1fr !important; }
      [data-grid2] { grid-template-columns: 1fr !important; }
      section { padding-left: 20px !important; padding-right: 20px !important; }
    }
  `;

  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>${sharedCSS}${responsiveCSS}body{background:${s.bg};color:${s.text};}</style></head><body>${heroSection}${featuresSection}${aboutSection}${productsBlock}${testimonialsSection}${ctaSection}${footerSection}${revealScript}</body></html>`;
}

// ======================== DARK LUXE RENDERER ========================
// Completely different from Business Pro: dark gradient bg, glow effects,
// centered hero (no split), alternating feature cards, glassmorphism

export function renderDarkLuxeTemplate(sections, style, productHtml = '') {
  const s = {
    primary: style.primary || '#A78BFA',
    accent: style.accent || '#38BDF8',
    bg: style.bg || '#09090B',
    text: style.text || '#FAFAFA',
    fh: style.fontHeading || 'Montserrat',
    fb: style.fontBody || 'Inter',
  };

  const iconSvg = (name, color) => {
    const icons = {
      zap: `<path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>`,
      shield: `<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>`,
      star: `<polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>`,
      target: `<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>`,
      users: `<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>`,
      gift: `<polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>`,
    };
    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${icons[name] || icons.star}</svg>`;
  };

  const stars = (count) => Array(count).fill('').map(() => `<svg width="14" height="14" viewBox="0 0 24 24" fill="${s.accent}" stroke="none"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></svg>`).join('');

  // HERO — Centered, large text, gradient glow orbs, NO image (text-focused)
  const heroSection = sections.hero?.enabled !== false ? `
    <section style="position:relative;min-height:100vh;display:flex;align-items:center;justify-content:center;overflow:hidden;background:${s.bg};">
      <div style="position:absolute;top:10%;left:20%;width:500px;height:500px;border-radius:50%;background:${s.primary};opacity:0.07;filter:blur(120px);pointer-events:none;"></div>
      <div style="position:absolute;bottom:15%;right:15%;width:400px;height:400px;border-radius:50%;background:${s.accent};opacity:0.05;filter:blur(100px);pointer-events:none;"></div>
      <div style="position:absolute;top:50%;left:50%;width:200px;height:200px;border-radius:50%;background:${s.primary};opacity:0.1;filter:blur(80px);pointer-events:none;transform:translate(-50%,-50%);"></div>
      <div style="position:relative;z-index:1;max-width:800px;margin:0 auto;padding:60px 24px;text-align:center;animation:fadeInUp 0.9s ease forwards;">
        <div style="display:inline-block;padding:8px 20px;border-radius:100px;border:1px solid ${s.primary}40;color:${s.primary};font-size:13px;font-weight:600;font-family:'${s.fb}',sans-serif;margin-bottom:32px;letter-spacing:1px;backdrop-filter:blur(8px);background:${s.primary}10;">${sections.hero.cta_text || 'GET STARTED'}</div>
        <h1 style="font-family:'${s.fh}',sans-serif;font-size:clamp(40px,6vw,72px);font-weight:800;line-height:1.05;color:${s.text};margin-bottom:24px;letter-spacing:-1px;">${sections.hero.headline || ''}</h1>
        <p style="font-family:'${s.fb}',sans-serif;font-size:18px;line-height:1.7;color:${s.text}77;margin-bottom:40px;max-width:600px;margin-left:auto;margin-right:auto;">${sections.hero.subheadline || ''}</p>
        <div style="display:flex;gap:16px;justify-content:center;flex-wrap:wrap;">
          <a href="${sections.hero.cta_url || '#products'}" class="btn-hover" style="display:inline-flex;align-items:center;gap:8px;padding:16px 40px;background:linear-gradient(135deg,${s.primary},${s.accent});color:#fff;border-radius:12px;font-family:'${s.fb}',sans-serif;font-size:16px;font-weight:600;cursor:pointer;box-shadow:0 0 30px ${s.primary}30;">${sections.hero.cta_text || 'Get Started'} →</a>
        </div>
      </div>
    </section>` : '';

  // FEATURES — Glassmorphism cards, 2-column with glow borders
  const featuresSection = sections.features?.enabled !== false ? `
    <section class="reveal" style="padding:100px 24px;background:${s.bg};position:relative;">
      <div style="max-width:1000px;margin:0 auto;text-align:center;">
        <h2 style="font-family:'${s.fh}',sans-serif;font-size:clamp(28px,4vw,42px);font-weight:700;color:${s.text};margin-bottom:16px;">${sections.features.title || ''}</h2>
        <p style="font-family:'${s.fb}',sans-serif;font-size:16px;color:${s.text}55;margin-bottom:56px;">${sections.features.subtitle || ''}</p>
        <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:20px;">
          ${(sections.features.items || []).map((item, i) => `
            <div class="reveal" style="padding:32px;border-radius:16px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.06);text-align:left;backdrop-filter:blur(12px);transition:all 0.3s ease;">
              <div style="width:48px;height:48px;border-radius:12px;background:linear-gradient(135deg,${s.primary}20,${s.accent}20);display:flex;align-items:center;justify-content:center;margin-bottom:16px;box-shadow:0 0 20px ${s.primary}15;">${iconSvg(item.icon, s.primary)}</div>
              <h3 style="font-family:'${s.fh}',sans-serif;font-size:17px;font-weight:600;color:${s.text};margin-bottom:8px;">${item.title}</h3>
              <p style="font-family:'${s.fb}',sans-serif;font-size:14px;line-height:1.6;color:${s.text}66;">${item.desc}</p>
            </div>`).join('')}
        </div>
      </div>
    </section>` : '';

  // ABOUT — Full-width image with overlay text (different from Business Pro's side-by-side)
  const aboutSection = sections.about?.enabled !== false ? `
    <section class="reveal" style="padding:80px 24px;background:${s.bg};">
      <div style="max-width:1000px;margin:0 auto;position:relative;border-radius:24px;overflow:hidden;">
        <img src="${sections.about.image || ''}" alt="" style="width:100%;aspect-ratio:21/9;object-fit:cover;display:block;" />
        <div style="position:absolute;inset:0;background:linear-gradient(to top,${s.bg} 10%,transparent 70%);"></div>
        <div style="position:absolute;bottom:0;left:0;right:0;padding:40px;text-align:center;">
          <h2 style="font-family:'${s.fh}',sans-serif;font-size:clamp(24px,3vw,32px);font-weight:700;color:#fff;margin-bottom:12px;">${sections.about.title || ''}</h2>
          <p style="font-family:'${s.fb}',sans-serif;font-size:15px;line-height:1.7;color:rgba(255,255,255,0.7);max-width:600px;margin:0 auto;">${sections.about.text || ''}</p>
        </div>
      </div>
    </section>` : '';

  const productsBlock = productHtml ? `
    <section id="products" class="reveal" style="padding:80px 24px;background:${s.bg};">
      <div style="max-width:1000px;margin:0 auto;text-align:center;">
        <h2 style="font-family:'${s.fh}',sans-serif;font-size:32px;font-weight:700;color:${s.text};margin-bottom:48px;">Products</h2>
        ${productHtml}
      </div>
    </section>` : '';

  // TESTIMONIALS — Horizontal cards with glass effect
  const testimonialsSection = sections.testimonials?.enabled !== false ? `
    <section class="reveal" style="padding:100px 24px;background:${s.bg};">
      <div style="max-width:1000px;margin:0 auto;text-align:center;">
        <h2 style="font-family:'${s.fh}',sans-serif;font-size:clamp(28px,3.5vw,36px);font-weight:700;color:${s.text};margin-bottom:48px;">${sections.testimonials.title || ''}</h2>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:20px;">
          ${(sections.testimonials.items || []).map(t => `
            <div style="padding:28px;border-radius:16px;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);text-align:left;backdrop-filter:blur(8px);">
              <div style="display:flex;gap:2px;margin-bottom:14px;">${stars(t.rating || 5)}</div>
              <p style="font-family:'${s.fb}',sans-serif;font-size:14px;line-height:1.7;color:${s.text}88;margin-bottom:16px;">"${t.text}"</p>
              <p style="font-family:'${s.fh}',sans-serif;font-size:13px;font-weight:600;color:${s.primary};">${t.name}</p>
              ${t.role ? `<p style="font-size:11px;color:${s.text}44;font-family:'${s.fb}',sans-serif;">${t.role}</p>` : ''}
            </div>`).join('')}
        </div>
      </div>
    </section>` : '';

  // CTA — Gradient border card (different from Business Pro's solid bg)
  const ctaSection = sections.cta?.enabled !== false ? `
    <section class="reveal" style="padding:100px 24px;background:${s.bg};">
      <div style="max-width:700px;margin:0 auto;padding:60px 40px;border-radius:24px;text-align:center;position:relative;background:linear-gradient(135deg,${s.primary}10,${s.accent}10);border:1px solid ${s.primary}20;">
        <div style="position:absolute;top:-1px;left:30%;right:30%;height:2px;background:linear-gradient(90deg,transparent,${s.primary},${s.accent},transparent);border-radius:2px;"></div>
        <h2 style="font-family:'${s.fh}',sans-serif;font-size:clamp(26px,3.5vw,38px);font-weight:700;color:${s.text};margin-bottom:12px;">${sections.cta.title || ''}</h2>
        <p style="font-family:'${s.fb}',sans-serif;font-size:16px;color:${s.text}66;margin-bottom:32px;">${sections.cta.subtitle || ''}</p>
        <a href="${sections.cta.button_url || '#products'}" class="btn-hover" style="display:inline-flex;align-items:center;gap:8px;padding:16px 40px;background:linear-gradient(135deg,${s.primary},${s.accent});color:#fff;border-radius:12px;font-family:'${s.fb}',sans-serif;font-size:16px;font-weight:600;cursor:pointer;box-shadow:0 0 30px ${s.primary}25;">${sections.cta.button_text || 'Get Started'} →</a>
      </div>
    </section>` : '';

  const footerSection = `
    <footer style="padding:32px 24px;background:${s.bg};border-top:1px solid rgba(255,255,255,0.05);text-align:center;">
      <p style="font-family:'${s.fb}',sans-serif;font-size:12px;color:rgba(255,255,255,0.25);">${sections.footer?.text || `© ${new Date().getFullYear()} All rights reserved`}</p>
    </footer>`;

  const responsiveCSS = `
    @media(max-width:768px) {
      section [style*="grid-template-columns:repeat(2"] { grid-template-columns: 1fr !important; }
      section [style*="grid-template-columns:repeat(3"] { grid-template-columns: 1fr !important; }
    }
  `;

  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>${sharedCSS}${responsiveCSS}body{background:${s.bg};color:${s.text};}</style></head><body>${heroSection}${featuresSection}${aboutSection}${productsBlock}${testimonialsSection}${ctaSection}${footerSection}${revealScript}</body></html>`;
}


// Template catalog — only 2 for now (truly unique from each other)
export const PREMIUM_TEMPLATE_CATALOG = [
  {
    id: 'business-clean',
    name: 'Business Pro',
    niche: 'business',
    description: 'Clean, professional layout — light theme, split hero, modern',
    defaultPalette: 'ocean',
    defaultFont: 'modern',
    thumbnail: NICHE_PHOTOS.business[0],
    renderer: 'default',
  },
  {
    id: 'dark-luxe',
    name: 'Dark Luxe',
    niche: 'business',
    description: 'Dark gradient with glow effects — inspired by premium SaaS sites',
    defaultPalette: 'dark',
    defaultFont: 'bold',
    thumbnail: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80',
    renderer: 'darkLuxe',
  },
];
